import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

import { PROFILE_IDS } from '@/core/constants/profileIds';

import {
  IconComponent,
  ObserveIntersectingDirective,
  ProgressComponent
} from '@factor_ec/ui';

import { SimulationService } from '@/main/services/simulation.service';
import { PaymentTable, Simulation } from '@/main/models/simulation';
import { CreditService } from '@/main/services/credit.service';
import { AppService } from '@/core/app.service';
import { ApiService } from '@/core/api.service';
import { environment } from '@/environments/environment';
import { Subscription } from 'rxjs';
import { SessionService } from '@/core/session.service';
import { IsGrantedDirective } from '@/core/directives/is-granted.directive';

/**
 * @description Componente para mostrar los detalles de una simulación de crédito
 * @class SimulationDetailComponent
 * @implements {OnInit, OnDestroy}
 */
@Component({
  selector: 'app-simulation-detail',
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatMenuModule,
    MatTooltipModule,
    IconComponent,
    ObserveIntersectingDirective,
    ProgressComponent,
    IsGrantedDirective
  ],
  templateUrl: './simulation-detail.component.html',
  styleUrl: './simulation-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SimulationDetailComponent implements OnInit, OnDestroy {
  // Dependency injection
  appService = inject(AppService);
  apiService = inject(ApiService);
  creditService = inject(CreditService);
  data: {
    simulation?: Simulation;
  } = inject(MAT_DIALOG_DATA);
  dialogRef: MatDialogRef<SimulationDetailComponent> = inject(MatDialogRef);
  sessionService = inject(SessionService);
  simulationService = inject(SimulationService);
  private subscriptions = new Subscription();

  // Properties
  currencyId = signal<string>('');
  simulation = signal<Simulation | undefined>(undefined);
  today = new Date();
  pageLoading = signal<boolean>(false);
  pageLimit = environment.pageLimit;
  pageNumber = 1;
  pageCompleted = signal<boolean>(false);
  public readonly totalToReceive = $localize`Total to receive`;
  public isBeneficiary = signal<boolean>(
    this.sessionService.params()['profileId'] === PROFILE_IDS.BasicBeneficiary
  );

  /**
   * @description Inicializa el componente
   * @implements OnInit
   */
  async ngOnInit(): Promise<void> {
    const operationNumber = this.isBeneficiary()
      ? this.data?.simulation?.operation.number
      : this.data?.simulation?.operationNumber;
    if (operationNumber) {
      const countries = this.sessionService
        .catalogs()
        .filter(c => c.catalogId === 'CPAMD');
      this.currencyId.set(
        countries?.find(c => c.id === this.data?.simulation?.countryId)
          ?.value || ''
      );
      this.simulation.set(structuredClone(this.data?.simulation));
      this.simulationService.change.subscribe((operation: any) => {
        this.dialogRef.close();
      });
      const paymentTable = await this.getPaymentTable(operationNumber, 1);
      this.simulation.update((simulation: Simulation | undefined) => {
        if (simulation) {
          simulation.paymentTable = paymentTable;
        }
        return simulation;
      });
    }
  }

  /**
   * @description Limpia las suscripciones al destruir el componente
   * @implements OnDestroy
   */
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * @description Obtiene la tabla de pagos de la simulación
   * @param {string} operationNumber - Número de operación
   * @param {number} pageNumber - Número de página
   * @returns {Promise<PaymentTable[]>} Tabla de pagos
   */
  async getPaymentTable(
    operationNumber: string,
    pageNumber: number
  ): Promise<PaymentTable[]> {
    this.pageLoading.set(true);
    const response: any = await this.apiService.call({
      microServiceName: 'WSMPCreditManagement',
      resourcePath: `/offercredit/get-amort-table/{operationNumber}/${this.pageLimit}/${pageNumber}`,
      method: 'GET',
      useToken: true,
      pathParams: {
        operationNumber: {
          value: operationNumber,
          encrypted: true
        }
      },
      responseEncrypted: true
    });
    this.pageLoading.set(false);
    this.pageCompleted.set(response.data.length < this.pageLimit);
    this.pageNumber = pageNumber;
    return response.data;
  }

  /**
   * @description Carga la siguiente página de la tabla de pagos
   * @param {any} event - Evento de scroll
   * @returns {Promise<void>}
   */
  async nextPage(event: any): Promise<void> {
    if (
      event &&
      !this.pageCompleted() &&
      !this.pageLoading() &&
      (this.simulation()?.paymentTable.length || 0) > 0
    ) {
      const items = await this.getPaymentTable(
        this.simulation()?.operationNumber || '',
        this.pageNumber + 1
      );
      this.simulation.update((simulation: Simulation | undefined) => {
        if (simulation) {
          simulation.paymentTable = simulation.paymentTable.concat(items);
        }
        return simulation;
      });
    }
  }
}
